// src/pages/Admin/Vote/VotePage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import { getAllVotes, deleteVote } from "./../../../services/voteServices";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import ButtonEdit from "./../../../components/Button/ButtonEdit";
import ButtonDelete from "./../../../components/Button/ButtonDelete";
import Button from "./../../../components/Button/Button";
import { FaPlus, FaSearch, FaVoteYea, FaCalendarAlt, FaSync } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";

import EditVoteModal from "./EditVoteModal";
import AddVoteModal from "./AddVoteModal";
import VoteQuestionsModal from "./VoteQuestionsModal";
import VoteResultsModal from "./VoteResultsModal";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date)) return d;
  const opts = { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" };
  return date.toLocaleDateString("fr-FR", opts).replace(",", " -");
};

const statusInfo = (vote) => {
  const now = new Date();
  const start = vote?.start_time ? new Date(vote.start_time) : null;
  const end = vote?.end_time ? new Date(vote.end_time) : null;

  if (start && now < start) {
    return { label: "Programmé", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" };
  }
  if (end && now > end) {
    return { label: "Clôturé", cls: "bg-slate-200 text-slate-800 dark:bg-slate-800/60 dark:text-slate-200" };
  }
  if (vote?.is_active) {
    return { label: "Actif", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" };
  }
  return { label: "Inactif", cls: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200" };
};

const typeBadge = (type) => {
  switch ((type || "").toLowerCase()) {
    case "sondage":
      return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200";
    case "vote":
    default:
      return "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200";
  }
};

export default function VotePage() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [voteData, setVoteData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentVote, setCurrentVote] = useState(null);

  const [openQuestionsModal, setOpenQuestionsModal] = useState(false);
  const [openResultsModal, setOpenResultsModal] = useState(false);
  const [selectedVoteId, setSelectedVoteId] = useState(null);
  const [selectedVoteTitle, setSelectedVoteTitle] = useState("");

  const fetchVotes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllVotes();
      setVoteData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur chargement des votes :", error);
      toast.error("Erreur lors de la récupération des votes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVotes();
  }, [fetchVotes]);

  const filteredVotes = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return voteData;
    return voteData.filter((v) =>
      `${v.title || ""} ${v.description || ""} ${v.type || ""}`.toLowerCase().includes(q)
    );
  }, [voteData, searchTerm]);

  const handleEditClick = (vote) => {
    setCurrentVote(vote);
    setShowEditModal(true);
  };

  const handleDeleteClick = async (id) => {
    if (!window.confirm("Supprimer ce vote/sondage ?")) return;
    try {
      await deleteVote(id);
      setVoteData((prev) => prev.filter((v) => v.id !== id));
      toast.success("Vote supprimé avec succès");
    } catch (error) {
      console.error("Erreur suppression vote :", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleOpenQuestionsModal = (voteId, title) => {
    setSelectedVoteId(voteId);
    setSelectedVoteTitle(title || "");
    setOpenQuestionsModal(true);
  };

  const handleOpenResultsModal = (voteId, title) => {
    setSelectedVoteId(voteId);
    setSelectedVoteTitle(title || "");
    setOpenResultsModal(true);
  };

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
                <HeaderTitle
                  role="Administrateur"
                  title="Gestion des Votes"
                  align="center"
                  className="mb-1"
                />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Bandeau gradient */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center text-white">
                  <FaVoteYea />
                </div>
                <div>
                  <p className="text-white text-lg sm:text-xl font-semibold">
                    Crée, planifie et suis tes votes & sondages
                  </p>
                  <p className="text-white/90 text-sm">
                    Ajoute des questions, consulte les résultats, modère et supprime si besoin.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="w-full sm:w-auto">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Rechercher titre, description, type…"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full sm:w-80 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  </div>
                </div>

                <Button
                  text="Ajouter un vote / sondage"
                  icon={FaPlus}
                  onClick={() => setShowAddModal(true)}
                  className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-2"
                />
                {/* Bouton Refresh page */}
                <Button
                  text="Rafraîchir"
                  icon={FaSync}
                  onClick={fetchVotes}
                  className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-2"
                />
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="p-4 sm:p-6">
            {/* Loading skeleton */}
            {loading && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
                  />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && filteredVotes.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun vote trouvé
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Ajuste ta recherche ou crée un nouveau vote.
                </p>
              </div>
            )}

            {/* Table desktop */}
            {!loading && filteredVotes.length > 0 && (
              <div className="hidden md:block">
                <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                          {["Titre", "Type", "Période", "Statut", "Questions", "Votants", "Actions"].map((h) => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                        {filteredVotes.map((v) => {
                          const st = statusInfo(v);
                          return (
                            <tr
                              key={v.id}
                              className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                            >
                              {/* Titre + description en tooltip */}
                              <td className="px-4 py-3 align-top">
                                <div className="font-medium text-slate-900 dark:text-slate-100">
                                  {v.title || "—"}
                                </div>
                                {!!v.description && (
                                  <div
                                    className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2"
                                    title={v.description}
                                  >
                                    {v.description}
                                  </div>
                                )}
                              </td>

                              {/* Type */}
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${typeBadge(v.type)}`}>
                                  {(v.type || "Vote").charAt(0).toUpperCase() + (v.type || "Vote").slice(1)}
                                </span>
                              </td>

                              {/* Période */}
                              <td className="px-4 py-3">
                                <div className="inline-flex items-center gap-2 text-slate-700 dark:text-slate-300">
                                  <FaCalendarAlt className="opacity-70" />
                                  <span className="whitespace-nowrap">{fmtDate(v.start_time)}</span>
                                  <span className="opacity-60">→</span>
                                  <span className="whitespace-nowrap">{fmtDate(v.end_time)}</span>
                                </div>
                              </td>

                              {/* Statut */}
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${st.cls}`}>
                                  {st.label}
                                </span>
                              </td>

                              {/* Questions */}
                              <td className="px-4 py-3">{v.questionCount ?? 0}</td>

                              {/* Votants */}
                              <td className="px-4 py-3">{v.voterCount ?? 0}</td>

                              {/* Actions */}
                              <td className="px-4 py-3">
                                <div className="flex flex-wrap items-center gap-2">
                                  <ButtonEdit onClick={() => handleEditClick(v)} />
                                  <ButtonDelete onClick={() => handleDeleteClick(v.id)} />
                                  <Button
                                    text="Questions"
                                    className="!rounded-xl !px-3 !py-1.5 text-xs"
                                    onClick={() => handleOpenQuestionsModal(v.id, v.title)}
                                  />
                                  <Button
                                    text="Résultats"
                                    className="!rounded-xl !px-3 !py-1.5 text-xs"
                                    onClick={() => handleOpenResultsModal(v.id, v.title)}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Cartes mobile */}
            {!loading && filteredVotes.length > 0 && (
              <div className="md:hidden space-y-3">
                {filteredVotes.map((v) => {
                  const st = statusInfo(v);
                  return (
                    <div
                      key={`card-${v.id}`}
                      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {v.title || "—"}
                        </div>
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${typeBadge(v.type)}`}>
                          {(v.type || "Vote").charAt(0).toUpperCase() + (v.type || "Vote").slice(1)}
                        </span>
                      </div>

                      {!!v.description && (
                        <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                          {v.description}
                        </p>
                      )}

                      <div className="mt-2 flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
                        <FaCalendarAlt className="opacity-70" />
                        <span className="whitespace-nowrap">{fmtDate(v.start_time)}</span>
                        <span className="opacity-60">→</span>
                        <span className="whitespace-nowrap">{fmtDate(v.end_time)}</span>
                      </div>

                      <div className="mt-2 flex items-center gap-2 text-xs">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-semibold ${st.cls}`}>
                          {st.label}
                        </span>
                        <span className="text-slate-600 dark:text-slate-300">
                          {v.questionCount ?? 0} question(s) • {v.voterCount ?? 0} votant(s)
                        </span>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <ButtonEdit onClick={() => setShowEditModal(true) || setCurrentVote(v)} />
                        <ButtonDelete onClick={() => handleDeleteClick(v.id)} />
                        <Button
                          text="Questions"
                          className="!rounded-xl !px-3 !py-1.5 text-xs"
                          onClick={() => handleOpenQuestionsModal(v.id, v.title)}
                        />
                        <Button
                          text="Résultats"
                          className="!rounded-xl !px-3 !py-1.5 text-xs"
                          onClick={() => handleOpenResultsModal(v.id, v.title)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <ToastContainer />
      </div>

      {/* Modales */}
      {showEditModal && (
        <EditVoteModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          vote={currentVote}
          refreshList={fetchVotes}
        />
      )}
      {showAddModal && (
        <AddVoteModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          refreshList={fetchVotes}
        />
      )}
      {openQuestionsModal && (
        <VoteQuestionsModal
          isOpen={openQuestionsModal}
          onClose={() => {
            setOpenQuestionsModal(false);
            setSelectedVoteId(null);
            setSelectedVoteTitle("");
          }}
          voteId={selectedVoteId}
          voteTitle={selectedVoteTitle}
        />
      )}
      {openResultsModal && (
        <VoteResultsModal
          isOpen={openResultsModal}
          onClose={() => setOpenResultsModal(false)}
          voteId={selectedVoteId}
          voteTitle={selectedVoteTitle}
        />
      )}
    </section>
  );
}
